import { useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { TransactionPostingWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { BaseAlertModal } from "../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../assets";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import Cookies from "universal-cookie";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { BaseButton } from "../../../components/button/styled";
import { postTransactionService } from "../../../utils/api/transaction/transactionposting/postTransaction";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import { useQuery } from "@tanstack/react-query";
import { retrieveInstitutionByIdService } from "../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCurrencyByIdService } from "../../../utils/outbound/currency/retrieveCurrencyById";
import { retrieveSavingsAccountByAccountNumberService } from "../../../utils/outbound/savingsaccount/retrieveSavingsAccountByAccountNumber";
import { retrieveCustomerService } from "../../../utils/api/customerservice/customerlist/retrieveCustomer";
import { useDebounce } from "use-debounce";
import { formatAmountDisplay } from "../../../helpers/formatAmountDisplay";
import { retrieveAllPaymentMethodsService } from "../../../utils/api/configuration/paymentmethod/retrieveAllPaymentMethods";

export const TransactionPosting = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	type FormDetails = {
		bankAccountReference: string;
		depositMethod: string;
		type: string;
		amount: string;
		description: string;
		paymentMethod?: string;
	};

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const depositMethods = ["bank", "cash"];
	const transactionTypes = ["deposit", "withdrawal"];

	const [isPostingTransaction, setIsPostingTransaction] = useState(false);
	const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [customerAccountDetails, setCustomerAccountDetails] = useState<any>({});
	const [formDetails, setFormDetails] = useState<FormDetails>({
		bankAccountReference: "",
		depositMethod: " ",
		type: " ",
		amount: "",
		description: "",
	});
	const [debouncedAccountRef] = useDebounce(
		formDetails.bankAccountReference,
		500,
	);

	useEffect(() => {
		setActiveNavItem("Transactions");
	}, [setActiveNavItem]);

	useEffect(() => {
		if (!debouncedAccountRef) return;
		const controller = new AbortController();
		let isActive = true;
		const fetchBankAccountInfo = async () => {
			try {
				setIsLoadingCustomer(true);
				setCustomerAccountDetails({});
				const savingsAccount =
					await retrieveSavingsAccountByAccountNumberService(
						TOKEN,
						debouncedAccountRef,
						controller.signal,
					);
				if (!isActive) return;
				if (!savingsAccount) {
					setCustomerAccountDetails({});
					return;
				}
				const customer = await retrieveCustomerService(
					TOKEN,
					savingsAccount.ownerId,
					controller.signal,
				);
				if (!isActive) return;
				setCustomerAccountDetails({
					...savingsAccount,
					customerName: `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`,
				});
			} catch (err: any) {
				if (err.name !== "AbortError") {
					console.error("Failed to fetch customer:", err);
					setCustomerAccountDetails({});
				}
			} finally {
				if (isActive) setIsLoadingCustomer(false);
			}
		};
		fetchBankAccountInfo();
		return () => {
			isActive = false;
			controller.abort();
		};
	}, [TOKEN, debouncedAccountRef]);

	const { data: paymentMethods } = useQuery({
		queryKey: ["paymentMethodsForTransactionPosting", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllPaymentMethodsService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: transactingTeller } = useQuery({
		queryKey: ["transactingTeller", TOKEN],
		queryFn: async () => {
			return await retrieveUserProfileService(TOKEN);
		},
		enabled: !!TOKEN,
	});

	const { data: currency } = useQuery({
		queryKey: [
			"transactingCurrencySymbol",
			transactingTeller?._id,
			transactingTeller?.institution,
		],
		queryFn: async () => {
			if (!transactingTeller) return null;
			const institution = await retrieveInstitutionByIdService(
				transactingTeller._id,
				transactingTeller.institution,
			);
			const currencyDetails = await retrieveCurrencyByIdService(
				transactingTeller._id,
				institution?.currency,
			);
			return currencyDetails;
		},
		enabled: !!transactingTeller,
	});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		return navigate(-1);
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => {
			const updated = {
				...prev,
				[name]: value,
			};
			if (name === "depositMethod") {
				if (value === "bank") {
					updated.paymentMethod = " ";
				} else {
					delete updated.paymentMethod;
				}
			}
			return updated;
		});
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsPostingTransaction(true);
		try {
			const response = await postTransactionService(TOKEN, formDetails);
			if (response.status === "success") {
				setIsPostingTransaction(false);
				setIsAlertModalOpen(true);
			} else {
				setIsPostingTransaction(false);
				setError(
					`Failed to post transaction. Please check credentials and try again.`,
				);
			}
		} catch (error: any) {
			setIsPostingTransaction(false);
			setError(`Failed to post transaction. ${error.message}`);
			console.error(`Failed to post transaction:`, error);
		}
	};

	return (
		<AppLayout pageId={"Transaction-Posting"} pageTitle={"Transaction Posting"}>
			<TransactionPostingWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title={"Transaction Posting Successful"}
					message="Your transaction has been successfully posted."
					callToAction="OK"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<Typography
						variant="h3"
						fontFamily={"Poppins"}
						fontWeight={500}
						fontSize={18}
						lineHeight={"normal"}
						color="var(--text-salmon-pink-color)"
					>
						Enter required information to post transaction.
					</Typography>
				</Box>
				<Stack
					direction={{ mobile: "column-reverse", laptop: "row" }}
					gap={{
						mobile: "var(--flex-gap)",
						laptop: "calc(var(--flex-gap) * 2)",
					}}
					alignItems={{ laptop: "flex-start" }}
					justifyContent={{ laptop: "space-between" }}
				>
					<form onSubmit={handleSubmit}>
						<Grid2
							container
							component={"div"}
							spacing={"var(--flex-gap)"}
							className="fieldset-grid-wrapper"
						>
							<Grid2 size={{ mobile: 12 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">
										Account Number
									</BaseLabel>
									<BaseInput
										required
										name="bankAccountReference"
										value={formDetails.bankAccountReference}
										placeholder="Enter Account Number"
										onChange={(e) => handleChange(e)}
									/>
								</BaseFieldSet>
							</Grid2>
							<Grid2 size={{ mobile: 12 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">
										Deposit/Withdrawal Method
									</BaseLabel>
									<BaseSelect
										name="depositMethod"
										fontweight={400}
										value={formDetails.depositMethod}
										colour="var(--semi-gray-color)"
										onChange={(e) => handleChange(e)}
									>
										<BaseOption value=" ">Select Option</BaseOption>
										{depositMethods?.map((method: string, index: number) => (
											<BaseOption key={index} value={method}>
												{method.charAt(0).toUpperCase() + method.slice(1)}
											</BaseOption>
										))}
									</BaseSelect>
								</BaseFieldSet>
							</Grid2>
							{formDetails.depositMethod === "bank" && (
								<Grid2 size={{ mobile: 12 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Payment Method
										</BaseLabel>
										<BaseSelect
											name="paymentMethod"
											fontweight={400}
											value={formDetails.paymentMethod}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value=" ">Select Option</BaseOption>
											{paymentMethods?.map(
												(method: Record<string, any>, index: number) => (
													<BaseOption key={index} value={method?.id}>
														{method?.name.charAt(0).toUpperCase() +
															method?.name.slice(1)}
													</BaseOption>
												),
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
							)}
							<Grid2 size={{ mobile: 12 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">
										Transaction Type
									</BaseLabel>
									<BaseSelect
										name="type"
										fontweight={400}
										value={formDetails.type}
										colour="var(--semi-gray-color)"
										onChange={(e) => handleChange(e)}
									>
										<BaseOption value=" ">Select Option</BaseOption>
										{transactionTypes?.map((type: string, index: number) => (
											<BaseOption key={index} value={type}>
												{type.charAt(0).toUpperCase() + type.slice(1)}
											</BaseOption>
										))}
									</BaseSelect>
								</BaseFieldSet>
							</Grid2>
							<Grid2 size={{ mobile: 12 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">Amount</BaseLabel>
									<BaseInput
										required
										name="amount"
										value={formDetails.amount}
										placeholder="Enter Amount"
										onChange={(e) => handleChange(e)}
									/>
								</BaseFieldSet>
							</Grid2>
							<Grid2 size={{ mobile: 12 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">Description</BaseLabel>
									<BaseInput
										required
										multiline
										minRows={4}
										name="description"
										value={formDetails.description}
										placeholder="Enter description."
										onChange={(e) => handleChange(e)}
									/>
								</BaseFieldSet>
							</Grid2>
						</Grid2>
						{error && (
							<Typography
								fontFamily={"Inter"}
								fontWeight={"600"}
								fontSize={14}
								lineHeight={"normal"}
								color={"var(--text-salmon-pink-color)"}
								whiteSpace={"normal"}
							>
								{error}
							</Typography>
						)}
						<Box overflow={"hidden"}>
							<BaseButton
								type="submit"
								variant="contained"
								bgcolor="var(--primary-color)"
								disableElevation
								sx={{
									width: { mobile: "100%", miniTablet: "auto" },
								}}
							>
								{isPostingTransaction ? (
									<CircularProgress color="inherit" className="loader" />
								) : (
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Post Transaction
									</Typography>
								)}
							</BaseButton>
						</Box>
					</form>
					<Card
						sx={{
							borderRadius: "1rem",
							flex: 0.4,
						}}
					>
						<CardContent className="card-content">
							<Stack
								height={"100%"}
								padding={"var(--basic-padding)"}
								gap={"var(--flex-gap)"}
							>
								<Box>
									{isLoadingCustomer ? (
										<CircularProgress color="inherit" className="loader" />
									) : (
										<Typography
											variant="h2"
											fontFamily={"Poppins"}
											fontWeight={600}
											fontSize={20}
											lineHeight={"normal"}
											color="var(--dark-color)"
										>
											{customerAccountDetails?.customerName}
										</Typography>
									)}
								</Box>
								<Stack>
									<Box>
										<Typography
											variant="subtitle1"
											fontFamily={"Poppins"}
											fontWeight={400}
											fontSize={16}
											lineHeight={"normal"}
											color="var(--primary-color)"
										>
											Account Balance:
										</Typography>
									</Box>
									<Box>
										{isLoadingCustomer ? (
											<CircularProgress color="inherit" className="loader" />
										) : (
											<Typography
												variant="body1"
												fontFamily={"Poppins"}
												fontWeight={600}
												fontSize={20}
												lineHeight={"normal"}
												color="var(--dark-color)"
											>
												{currency?.symbol || ""}
												{formatAmountDisplay(
													customerAccountDetails.balance ?? 0,
												)}
											</Typography>
										)}
									</Box>
								</Stack>
							</Stack>
						</CardContent>
					</Card>
				</Stack>
			</TransactionPostingWrapper>
		</AppLayout>
	);
};
